import { useEffect, useRef, useState } from "preact/hooks";
import emitter from "@webcore/core/events/eventemitter";
import { buildAppClipUrl } from "@core/hooks/useAppClipUrl";
import { QrCodeGenerator } from "@core/components/QrCodeGenerator";
import { Spinner } from "@core/components/Elements";
import { getExperienceMode } from "@core/utils/experienceMode";
import getUserDevice from "@core/utils/getUserDevice";
import type { ModelData } from '@core/content/types';
import viewInArIcon from "../assets/icons/view-in-ar-icon.svg";

interface ARButtonProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  isChecking: boolean;
  isCompatible: boolean;
  runtimeIsPreparing?: boolean;
  hideOnDesktop?: boolean;
}

export function ARButton({
  model,
  onLaunchAR,
  isChecking,
  isCompatible,
  runtimeIsPreparing = false,
  hideOnDesktop = false,
}: ARButtonProps) {
  const [showQRPopup, setShowQRPopup] = useState(false);
  const { device, isMobile } = getUserDevice();
  const isDesktop = !isMobile;
  const isIOS = device === 'iOS' || device === 'AppClip';
  const [isIOSWarmupLoading, setIsIOSWarmupLoading] = useState(isIOS);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const popupWidth = 250;
  const qrSize = 218;


  // Hide on desktop if requested
  if (hideOnDesktop && isDesktop) {
    return null;
  }

  const handleClick = () => {
    // Desktop: show QR popup (only open, click outside to close)
    if (isDesktop) {
      setShowQRPopup(true);
      return;
    }
    
    // iOS: hand off to the App Clip — unless this device is too old to run it
    // (e.g. iOS < 16.4, where useXRCheckInline reports NOT_SUPPORTED so
    // isCompatible is false). There's no in-browser WebXR fallback on iOS, so
    // surface the not-supported screen instead of redirecting into Apple's
    // "update your device" wall.
    if (isIOS) {
      if (!isCompatible) {
        (emitter as any).emit("xr-error-not-supported");
        return;
      }
      try {
        const u = new URL(window.location.href);
        u.hash = model.id;
        u.searchParams.set('fromAr', '1');
        window.history.pushState({}, '', u.toString());
      } catch {
        // ignore
      }
      const returnUrl = window.location.href;
      // Include /onsite path if mode is active (from URL or sessionStorage)
      const isOnsite = window.location.pathname.startsWith('/onsite') || getExperienceMode() === 'onsite';
      const baseUrl = isOnsite
        ? `${window.location.origin}/onsite`
        : window.location.origin + window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const appClipParams: Record<string, string> = {};
      const keysToForward = [
        'config',
      ];
      for (const key of keysToForward) {
        const value = searchParams.get(key);
        if (value !== null) appClipParams[key] = value;
      }
      window.location.href = buildAppClipUrl(model.id, {
        returnUrl,
        baseUrl,
        params: appClipParams,
      });
      return;
    }
    
    // Android: launch WebXR when compatible. When it isn't, surface the
    // appropriate error screen (mirrors ejc-world) instead of doing nothing —
    // 'xr-error-undefined' when the browser has no WebXR API at all (Firefox,
    // Opera, in-app webviews), 'xr-error-not-supported' when navigator.xr
    // exists but immersive-ar can't start (incapable device). Both are picked
    // up by useXRErrorGuidance and rendered by the error screen in App.tsx.
    if (isCompatible) {
      onLaunchAR(model);
    } else if (!navigator.xr) {
      (emitter as any).emit("xr-error-undefined");
    } else {
      (emitter as any).emit("xr-error-not-supported");
    }
  };

  // Build QR code URL
  const qrUrl = (() => {
    try {
      const u = new URL(window.location.href);
      u.hash = model.id;
      return u.toString();
    } catch {
      return window.location.href;
    }
  })();

  useEffect(() => {
    if (isMobile || !showQRPopup) {
      return;
    }

    const handleOutsideClick = (event: PointerEvent) => {
      if (!qrContainerRef.current?.contains(event.target as Node)) {
        setShowQRPopup(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, [isMobile, showQRPopup]);

  useEffect(() => {
    if (!isIOS) {
      setIsIOSWarmupLoading(false);
      return;
    }

    setIsIOSWarmupLoading(true);
    const timeoutId = window.setTimeout(() => {
      setIsIOSWarmupLoading(false);
    }, 2000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isIOS]);

  const isLoading = !isDesktop && (
    (isIOS && (isChecking || isIOSWarmupLoading)) ||
    (!isIOS && (runtimeIsPreparing || isChecking))
  );

  // Prevent background scroll while pre-launch checks/runtime prep are in progress.
  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isLoading]);

  // The button always stays active. While the XR check / runtime prep is in
  // progress it's replaced by a spinner (isLoading); once that resolves, an
  // incompatible device no longer greys the button out — clicking it surfaces
  // the appropriate error screen instead (see handleClick).
  const buttonStyle = "bg-transparent border-2 border-white text-white";

  return (
    <div ref={qrContainerRef} className="relative w-full max-w-[270px] min-h-[71px] md:max-w-[300px] md:min-h-[91px] h-full">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="2md" variant="light" />
        </div>
      ) : (
        <div className="w-full h-full">
          <button
            onClick={handleClick}
            className={`w-full h-full px-4 font-bold text-[22px] md:text-[26px] leading-[1.25] tracking-[0.02em] flex items-center justify-center gap-[22px] ${buttonStyle} hover:bg-white/10 active:bg-white/10`}
          >
            <>
              <span>VIEW IN AR</span>
              <img
                src={viewInArIcon}
                alt=""
                aria-hidden="true"
                className="h-[42px] w-[24px] md:h-[55px] md:w-[33px] shrink-0"
              />
            </>
          </button>
        </div>
      )}

      {/* QR Code popup - desktop only */}
      {isDesktop && (
        <div
          className={`absolute bottom-full left-1/2 mb-10 z-50 -translate-x-1/2 transform transition-all duration-300 ease-out ${
            showQRPopup
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "translate-y-5 opacity-0 pointer-events-none"
          }`}
        >
          <div
            className="bg-white rounded-[8px] flex flex-col items-center pt-1 px-1 pb-5"
            style={{
              width: `${popupWidth}px`,
              boxSizing: "border-box",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <QrCodeGenerator value={qrUrl} fgColor="#000000" size={qrSize} />
            <p className="text-black text-center text-lg tracking-[1.76px] font-[500] mt-2 mb-1">
              Scan QR code
            </p>
          </div>
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-[18px] w-0 h-0"
            style={{
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "18px solid white",
            }}
          />
        </div>
      )}
    </div>
  );
}
